import { Link } from 'react-router-dom';
import styles from './Legal.module.css';
import FinancialDataDisclaimer from './FinancialDataDisclaimer';
import {
  LEGAL_LAST_UPDATED,
  TODO_ENTITY,
  TODO_JURISDICTION,
  TODO_CONTACT_EMAIL,
} from './legalMeta';

// Terms of Service.
//
// Written as if user accounts exist (per maintainer directive — login is
// planned near-term; see legalMeta.js ACCOUNTS_ASSUMED), so account
// registration, account responsibilities, and termination are present-tense.
// Subscription/payment terms are CONDITIONAL ("if you purchase a paid
// subscription") because no payment processor is integrated in the codebase
// yet — this avoids asserting a billing arrangement we can't substantiate.
//
// The third-party-data section reflects our current licensing posture: FunViz
// itself must hold appropriate rights to display third-party market data, and
// USERS are prohibited from scraping, redistributing, or reselling that data.
// It does NOT claim FunViz holds redistribution rights or is "FMP compliant".
// Company/entity/jurisdiction/contact are clearly-marked placeholders.
const TermsOfService = () => (
  <main className={styles.page}>
    <article className={styles.content}>
      <header className={styles.header}>
        <h1 className={styles.title}>Terms of Service</h1>
        <p className={styles.updated}>Last updated: {LEGAL_LAST_UPDATED}</p>
      </header>

      <section className={styles.section}>
        <p>
          These Terms of Service (&ldquo;Terms&rdquo;) govern your use of the
          FunViz website and application (the &ldquo;Service&rdquo;), operated by{' '}
          <span className={styles.placeholder}>{TODO_ENTITY}</span>. By using the
          Service, you agree to these Terms. If you do not agree, do not use the
          Service. These Terms are provided for transparency and are not legal
          advice.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="tos-service">
        <h2 id="tos-service" className={styles.sectionHeading}>
          The Service
        </h2>
        <p>
          FunViz is a stock-analysis and visualization tool for long-term
          investors. It presents market and financial data and derived
          calculations (such as fair-value overlays, valuation ratios, and
          backtests) for informational and educational purposes only. The
          Service does not provide investment, financial, legal, or tax advice.
          See our <Link to="/disclaimer">Disclaimer</Link> for details.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="tos-accounts">
        <h2 id="tos-accounts" className={styles.sectionHeading}>
          Accounts and your responsibilities
        </h2>
        <ul>
          <li>
            You must provide accurate information when you register and keep it
            up to date.
          </li>
          <li>
            You are responsible for maintaining the confidentiality of your
            account credentials and for all activity that occurs under your
            account.
          </li>
          <li>
            You must promptly notify us of any unauthorized use of your account
            or any other breach of security.
          </li>
          <li>
            You must be legally able to enter into these Terms in your
            jurisdiction.
          </li>
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="tos-acceptable">
        <h2 id="tos-acceptable" className={styles.sectionHeading}>
          Acceptable use
        </h2>
        <p>You agree not to:</p>
        <ul>
          <li>
            Scrape, harvest, bulk-download, redistribute, resell, or otherwise
            make available the market or financial data or any derived data
            obtained through the Service, except as expressly permitted by these
            Terms.
          </li>
          <li>
            Use the Service in a way that infringes third-party rights, breaches
            any third-party data provider&rsquo;s terms, or violates applicable
            law.
          </li>
          <li>
            Interfere with or disrupt the Service, attempt to gain unauthorized
            access to it, or circumvent any access, rate, or usage limits.
          </li>
          <li>Use the Service to build or train a competing data product.</li>
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="tos-subscriptions">
        <h2 id="tos-subscriptions" className={styles.sectionHeading}>
          Subscriptions and payment
        </h2>
        <p>
          The Service may offer free and paid features. If you purchase a paid
          subscription, the applicable price, billing frequency, and any
          recurring-billing terms will be disclosed to you at the point of
          purchase before you are charged. Paid subscriptions are billed on a
          recurring basis until cancelled, and payment is handled by a
          third-party payment processor; we do not store your full payment card
          details. You may cancel a subscription as described at checkout or in
          your account settings, and cancellation takes effect at the end of the
          current billing period. Refund eligibility, if any, will be stated at
          the point of purchase.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="tos-thirdparty">
        <h2 id="tos-thirdparty" className={styles.sectionHeading}>
          Third-party data
        </h2>
        <FinancialDataDisclaimer variant="block" />
        <p>
          Market and financial data are owned by the third-party provider(s)
          from which they originate and are protected by intellectual-property
          and contractual rights. Your right to use the Service does not grant
          you any right to redistribute, resell, or create derivative datasets
          from that data. Data provided through the Service is subject to the
          applicable third-party provider&rsquo;s terms.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="tos-ip">
        <h2 id="tos-ip" className={styles.sectionHeading}>
          Intellectual property
        </h2>
        <p>
          The Service, including its software, design, and original content
          (excluding third-party data), is owned by{' '}
          <span className={styles.placeholder}>{TODO_ENTITY}</span> and is
          protected by applicable intellectual-property laws. We grant you a
          limited, revocable, non-exclusive, non-transferable licence to use the
          Service for its intended purpose, subject to these Terms.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="tos-disclaimer">
        <h2 id="tos-disclaimer" className={styles.sectionHeading}>
          Disclaimers and limitation of liability
        </h2>
        <p>
          The Service and all data and calculations are provided on an
          &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis, without
          warranties of any kind, to the fullest extent permitted by law. We do
          not warrant that the Service or any data will be accurate, complete,
          uninterrupted, or error-free. To the fullest extent permitted by law,
          we are not liable for any indirect, incidental, consequential, or
          special damages, or for any investment losses, arising from your use
          of the Service. Your use of the Service and any reliance on its
          information is at your own risk.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="tos-availability">
        <h2 id="tos-availability" className={styles.sectionHeading}>
          Service availability and changes
        </h2>
        <p>
          We may modify, suspend, or discontinue the Service or any feature at
          any time. We may also update these Terms; material changes will be
          reflected by updating the &ldquo;Last updated&rdquo; date above, and
          your continued use of the Service after changes take effect
          constitutes acceptance of the updated Terms.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="tos-termination">
        <h2 id="tos-termination" className={styles.sectionHeading}>
          Termination
        </h2>
        <p>
          You may stop using the Service and close your account at any time. We
          may suspend or terminate your access if you breach these Terms or use
          the Service in a way that could cause harm to us, other users, or a
          third-party data provider.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="tos-law">
        <h2 id="tos-law" className={styles.sectionHeading}>
          Governing law and contact
        </h2>
        <p>
          These Terms are governed by the laws of{' '}
          <span className={styles.placeholder}>{TODO_JURISDICTION}</span>.
          Questions about these Terms can be directed to{' '}
          <span className={styles.placeholder}>{TODO_CONTACT_EMAIL}</span>.
        </p>
      </section>

      <p>
        See also our{' '}
        <Link to="/privacy" className={styles.backLink}>Privacy Policy</Link> and{' '}
        <Link to="/disclaimer" className={styles.backLink}>Disclaimer</Link>.
      </p>
      <p>
        <Link to="/" className={styles.backLink}>&larr; Back to FunViz</Link>
      </p>
    </article>
  </main>
);

export default TermsOfService;
